<template>
  <div v-if="hasPopulationToPopulationSettings" class="p-8">
    <div class="c-population-to-population-settings-card">
      <div class="triangle-up"> </div>
      <div class="flex justify-between items-center">
        <p class="font-bold" data-testid="header-text">{{ pluralizedText }}</p>
        <BittsDropdown
          v-if="hasSharingPermission"
          :options="cardHeaderOptions"
          @selection="(val) => onOptionSelected(val)"
        >
          <template #prompt>
            <BittsButton
              :center-icon="['fas', 'ellipsis']"
              size="x-small"
              type="neutral"
              variant="outline"
            />
          </template>
        </BittsDropdown>
      </div>
      <BittsDivider class="my-12" />
      <div
        v-for="(partnerPopId, index) in partnerPopulationIds"
        :key="`partner-pop-${partnerPopId}-${index}`"
      >
        <BittsDivider
          v-if="index > 0"
          class="my-16"
          :class="{ 'mt-0': index === 0 }"
        />
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-8 text-neutral-text">
            <SharingSetting
              :data-share="getDataShare(partnerPopId)"
              :should-mount-to-body="false"
              :show-fields="true"
            />
            <p>
              when overlapping {{ partnerName }}'s
              <strong>{{ getPartnerPopulationName(partnerPopId) }}</strong>
            </p>
          </div>
          <BittsDropdown
            v-if="hasSharingOverrides && hasSharingPermission"
            :options="individualOptions"
            @selection="(val) => onOptionSelected(val, partnerPopId)"
          >
            <template #prompt>
              <BittsButton
                :center-icon="['fas', 'ellipsis']"
                size="x-small"
                type="neutral"
                variant="outline"
              />
            </template>
          </BittsDropdown>
          <BittsButton
            v-else-if="hasSharingPermission"
            @click="handleDeletePopulationToPopulationSetting(partnerPopId)"
            variant="outline"
            type="neutral"
            size="x-small"
            class="p-6"
          >
            <template #icon>
              <FontAwesomeIcon
                :icon="['fak', 'x']"
                :style="{
                  height: '12px',
                  width: '12px',
                  color: 'currentColor',
                }"
                class="text-danger-accent"
              />
            </template>
          </BittsButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsDivider, BittsDropdown } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { pluralize } from 'humanize-plus';
import { computed, nextTick, ref, watchEffect } from 'vue';

import { PopulationToPopulationMap } from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/composables/useSharingSettings';
import SharingSetting from '@/components/populations/SharingSetting.vue';

import useAuth from '@/composables/useAuth';
import usePopulations from '@/composables/usePopulations';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores';

const props = defineProps<{
  populationId: number;
  partnerName: string;
  partnerId: number;
  deleteOverride: (
    shareId: number,
    partnerPopulationId: number,
    populationId: number,
  ) => void;
  currentPopulationToPopulation: PopulationToPopulationMap;
}>();
const emit =
  defineEmits<(e: 'show-pop-overrides-modal' | 'reset-data-share') => void>();
const { hasFeatureFlag } = useFeatureFlagStore();
const hasSharingOverrides = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_NEW_PER_POP_OVERRIDES),
);

interface PopulationToPopulationSetting {
  partnerPopulation: {
    id: number;
    name: string;
  };
  dataShare: {
    id: number;
  };
}

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

const { customPopsWithStatsAndSharing, allPopulations } = usePopulations(
  props.partnerId as never,
);

const population = computed(() => {
  return [...allPopulations.value, ...customPopsWithStatsAndSharing.value].find(
    (pop) => pop.id === props.populationId,
  );
});

const populationToPopulationSettings = computed(() => {
  if (!population.value?.populationToPopulationSettings) return [];
  return population.value
    .populationToPopulationSettings as PopulationToPopulationSetting[];
});

const localPartnerPopIds = ref<number[]>([]);

watchEffect(() => {
  const popToPopSettings =
    props.currentPopulationToPopulation[props.populationId];
  if (!popToPopSettings) {
    localPartnerPopIds.value = [];
  } else {
    localPartnerPopIds.value = Object.keys(popToPopSettings).map(Number);
  }
});

const partnerPopulationIds = computed(() => localPartnerPopIds.value);

const hasPopulationToPopulationSettings = computed(() => {
  return partnerPopulationIds.value.length > 0;
});

function getDataShare(partnerPopId: number) {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  return setting?.dataShare || {};
}

function getPartnerPopulationName(partnerPopId: number): string {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  return setting?.partnerPopulation?.name || 'Unknown Population';
}

function handleDeletePopulationToPopulationSetting(partnerPopId: number) {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  if (setting?.dataShare?.id) {
    props.deleteOverride(
      setting.dataShare.id,
      partnerPopId,
      props.populationId,
    );
  }
}

const pluralizedText = computed(
  () =>
    `${partnerPopulationIds.value.length} ${pluralize(partnerPopulationIds.value.length, 'Override')}`,
);

const cardHeaderOptions = computed(() => {
  const optionsWithoutEdit = [
    {
      value: 'delete',
      display: `Remove ${pluralizedText.value.toLowerCase()}`,
      iconType: ['fak', 'x'],
    },
  ];
  const editOverrideOption = {
    value: 'edit',
    display: 'Edit',
    iconType: ['fak', 'edit'],
  };
  if (hasSharingOverrides.value) {
    optionsWithoutEdit.unshift(editOverrideOption);
  }
  return optionsWithoutEdit;
});

const individualOptions = computed(() => {
  const optionsWithoutEdit = [
    {
      value: 'delete',
      display: 'Remove override',
      iconType: ['fak', 'x'],
    },
  ];
  const editOverrideOption = {
    value: 'edit',
    display: 'Edit',
    iconType: ['fak', 'edit'],
  };
  if (hasSharingOverrides.value) {
    optionsWithoutEdit.unshift(editOverrideOption);
  }
  return optionsWithoutEdit;
});

async function onOptionSelected(
  { value }: { value: string },
  partnerPopId?: number,
) {
  if (value === 'delete') {
    if (partnerPopId) {
      handleDeletePopulationToPopulationSetting(partnerPopId);
    } else {
      partnerPopulationIds.value.forEach((popId) => {
        handleDeletePopulationToPopulationSetting(popId);
      });
    }
    await nextTick();
    if (!localPartnerPopIds.value.length) emit('reset-data-share');
  }
  if (value === 'edit') {
    emit('show-pop-overrides-modal');
  }
}
</script>
<style scoped lang="pcss">
.c-population-to-population-settings-card {
  @apply p-12 rounded-6 bg-neutral-background-weak relative;
}
.triangle-up {
  @apply absolute top-[-8px] w-0 h-0 left-8 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-neutral-background-weak mb-2;
}
</style>
